import { cn } from "cn";
import * as React from "react";

/** Допуск в пикселях: дробные `scrollLeft` на масштабе не должны оставлять «призрачное» затухание. */
const EDGE_TOLERANCE_PX = 1;

interface ScrollFadeMetrics {
  scrollLeft: number;
  clientWidth: number;
  scrollWidth: number;
}

interface ScrollFadeEdges {
  /** Слева есть скрытое: прокручено от начала. */
  start: boolean;
  /** Справа есть скрытое: можно прокручивать дальше. */
  end: boolean;
}

/** Затухание у края — только если за этим краем есть непоказанное. */
function scrollFadeEdges({ scrollLeft, clientWidth, scrollWidth }: ScrollFadeMetrics) {
  const start = scrollLeft > EDGE_TOLERANCE_PX;
  const end = scrollLeft + clientWidth < scrollWidth - EDGE_TOLERANCE_PX;
  return { start, end } satisfies ScrollFadeEdges;
}

/**
 * Горизонтально прокручиваемая полоса без видимой полосы прокрутки; у края, за
 * которым есть непоказанное, содержимое затухает. Состояние краёв — в
 * `data-fade-start` и `data-fade-end`. Содержимое не переносится: потомкам
 * нужны `shrink-0` и `whitespace-nowrap`.
 */
function ScrollFade({
  ref,
  className,
  contentClassName,
  children,
  onScroll,
  ...props
}: React.ComponentProps<"div"> & { contentClassName?: string }) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  // `ref` вызывающего не должен отнимать у полосы её собственный: без него
  // замер молчит и затухание не появляется никогда.
  const setRoot = React.useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [edges, setEdges] = React.useState<ScrollFadeEdges>({ start: false, end: false });

  const measure = React.useCallback(() => {
    const root = rootRef.current;
    if (root === null) {
      return;
    }
    const next = scrollFadeEdges(root);
    setEdges((prev) => (prev.start === next.start && prev.end === next.end ? prev : next));
  }, []);

  React.useLayoutEffect(() => {
    measure();
    const root = rootRef.current;
    const content = contentRef.current;
    if (root === null || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    if (content !== null) {
      observer.observe(content);
    }
    return () => observer.disconnect();
  }, [measure]);

  return (
    <div
      {...props}
      ref={setRoot}
      data-slot="scroll-fade"
      data-fade-start={edges.start}
      data-fade-end={edges.end}
      onScroll={(event) => {
        measure();
        onScroll?.(event);
      }}
      className={cn(
        // `scroll-px-6` — по ширине затухания: фишка, до которой дошли Tab'ом,
        // встаёт вне маски, а не полупрозрачной у самого края.
        "scroll-px-6 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        "data-[fade-start=true]:[--scroll-fade-start:24px] data-[fade-end=true]:[--scroll-fade-end:24px]",
        // Маска задаёт только прозрачность, цвет в ней не участвует.
        "data-[fade-start=true]:[mask-image:linear-gradient(to_right,transparent,black_var(--scroll-fade-start,0px),black_calc(100%-var(--scroll-fade-end,0px)),transparent)]",
        "data-[fade-end=true]:[mask-image:linear-gradient(to_right,transparent,black_var(--scroll-fade-start,0px),black_calc(100%-var(--scroll-fade-end,0px)),transparent)]",
        className,
      )}
    >
      <div
        ref={contentRef}
        data-slot="scroll-fade-content"
        className={cn("flex w-max min-w-full flex-nowrap items-center", contentClassName)}
      >
        {children}
      </div>
    </div>
  );
}

export { ScrollFade, scrollFadeEdges };
